import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import { adminAccessService, authService } from '../services'
import {
  formatPasswordGenerateCooldownMessage,
  isPasswordGenerateCooldownError,
} from '../utils/adminPasswordGenerate'

/**
 * Administration → Admin passwords.
 * Read lists accounts. Write generates a password. Superuser does not bypass this.
 */
const AdminPasswordsPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canRead = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ADMIN_PASSWORDS,
    'read',
  )
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.ADMIN_PASSWORDS,
    'write',
  )

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [usersData, setUsersData] = useState(null)
  const [generatingId, setGeneratingId] = useState(null)
  const [snackbar, setSnackbar] = useState(null)

  const [generatedPassword, setGeneratedPassword] = useState(null)
  const [generatedUser, setGeneratedUser] = useState(null)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminAccessService.listGoogleUsers({
        page: 1,
        limit: 100,
        search: search.trim() || undefined,
      })
      setUsersData(data)
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || 'Failed to load admin users',
      )
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    if (!canRead) return
    loadUsers()
  }, [canRead, loadUsers])

  if (!canRead) {
    return <Navigate to="/dashboard" replace />
  }

  const closeGeneratedDialog = () => {
    setGeneratedPassword(null)
    setGeneratedUser(null)
  }

  const handleGenerate = async (row) => {
    if (!row?.id) return
    setGeneratingId(row.id)
    try {
      const result = await authService.generateRandomPassword(row.id)
      setGeneratedPassword(result.password)
      setGeneratedUser({ id: row.id, email: row.email, name: row.name })
      setSnackbar({
        severity: 'success',
        message:
          result.message ||
          'Password generated. Copy it now — it will not be shown again.',
      })
    } catch (e) {
      const message = isPasswordGenerateCooldownError(e)
        ? formatPasswordGenerateCooldownMessage(e)
        : e.response?.data?.message || e.message || 'Failed to generate password'
      setSnackbar({ severity: 'warning', message })
    } finally {
      setGeneratingId(null)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <VpnKeyIcon color="primary" />
        Admin passwords
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a random password for an eligible admin account. The password is
        shown once — copy it securely. Existing sessions for that account are
        revoked. You can regenerate at most once every 2 minutes per account.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search email or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <Button variant="outlined" onClick={loadUsers} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Whitelisted</TableCell>
                <TableCell>Roles</TableCell>
                    {canWrite && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(usersData?.items || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite ? 5 : 4}>
                    <Typography variant="body2" color="text.secondary">
                      No admin users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (usersData?.items || []).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.name || '—'}</TableCell>
                    <TableCell>{row.whitelisted ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {(row.roleCodes || []).join(', ') || '—'}
                    </TableCell>
                    {canWrite && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VpnKeyIcon />}
                          disabled={!row.whitelisted || generatingId === row.id}
                          onClick={() => handleGenerate(row)}
                        >
                          {generatingId === row.id ? 'Generating…' : 'Generate password'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!generatedPassword}
        onClose={closeGeneratedDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Generated password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This password is shown only once. Copy it now. It will not be emailed
            or stored in plaintext.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            For <strong>{generatedUser?.email}</strong>
            {generatedUser?.name ? ` (${generatedUser.name})` : ''}:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'monospace',
                letterSpacing: '0.05em',
                flexGrow: 1,
                wordBreak: 'break-all',
              }}
            >
              {generatedPassword}
            </Typography>
            <Button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(generatedPassword)
                  setSnackbar({ severity: 'success', message: 'Password copied' })
                } catch {
                  setSnackbar({
                    severity: 'error',
                    message: 'Could not copy to clipboard',
                  })
                }
              }}
              startIcon={<ContentCopyIcon />}
              size="small"
              variant="outlined"
            >
              Copy
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeGeneratedDialog}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

export default AdminPasswordsPage
